import React, { useCallback, useEffect, useMemo, useRef } from 'react';
import { Marker } from 'react-mapbox-gl';
import type { GeoJSONSource } from 'mapbox-gl';
import { WaybillService } from '@/services';
import { WaybillTrackLogResponse } from '@/services/type';
import { useParams } from 'react-router-dom';
import { API_CODE, WAYBILL_STATUS } from '@/utils/constant';
import { useSyncState } from '@/hooks/useSyncState';
import Detail from './Detail';
import RouteInfo from './RouteInfo';
import Container, { useMapInstance } from './MapBoxContainer';

type Poi = { key: string; location: [number, number] | null };

const calculateHeading = (startLat: number, startLng: number, endLat: number, endLng: number) => {
  const toRad = (deg: number) => (deg * Math.PI) / 180;
  const toDeg = (rad: number) => (rad * 180) / Math.PI;

  const lat1 = toRad(startLat);
  const lat2 = toRad(endLat);
  const dLng = toRad(endLng - startLng);

  const y = Math.sin(dLng) * Math.cos(lat2);
  const x = Math.cos(lat1) * Math.sin(lat2) - Math.sin(lat1) * Math.cos(lat2) * Math.cos(dLng);

  const heading = toDeg(Math.atan2(y, x));
  return (heading + 360) % 360;
};

const MapContent = ({ id }: { id: string }) => {
  const [detail, setDetail, detailRef] = useSyncState<WaybillTrackLogResponse>();
  const map = useMapInstance();
  const startLocation = detail?.orderDetail?.orderAddress?.sendLocation?.split(',');
  const endLocation = detail?.orderDetail?.orderAddress?.receiveLocation?.split(',');
  const currentLocation = detail?.locationList?.[detail?.locationList?.length - 1];
  const preLocation = detail?.locationList?.[detail?.locationList?.length - 2];
  const timer = useRef<NodeJS.Timeout | null>(null);

  const startCoord = useMemo(() => {
    if (startLocation?.length !== 2) return null;
    const lng = Number(startLocation[0]);
    const lat = Number(startLocation[1]);
    if (Number.isNaN(lng) || Number.isNaN(lat)) return null;
    return [lng, lat] as [number, number];
  }, [startLocation]);

  const endCoord = useMemo(() => {
    if (endLocation?.length !== 2) return null;
    const lng = Number(endLocation[0]);
    const lat = Number(endLocation[1]);
    if (Number.isNaN(lng) || Number.isNaN(lat)) return null;
    return [lng, lat] as [number, number];
  }, [endLocation]);

  const locations: Poi[] = useMemo(() => {
    return [
      { key: 'start', location: startCoord },
      { key: 'end', location: endCoord },
    ];
  }, [startCoord, endCoord]);

  const heading = useMemo(() => {
    if (currentLocation?.latitude && currentLocation?.longitude && preLocation?.latitude && preLocation?.longitude) {
      return calculateHeading(
        preLocation.latitude,
        preLocation.longitude,
        currentLocation.latitude,
        currentLocation.longitude,
      );
    }
    return 0;
  }, [currentLocation, preLocation]);

  const trackCoordinates = useMemo(() => {
    if (!detail?.locationList?.length) return [];
    return (
      detail.locationList
        ?.map((item) => {
          const lng = Number(item.longitude);
          const lat = Number(item.latitude);
          if (!Number.isFinite(lng) || !Number.isFinite(lat)) return null;
          return [lng, lat] as [number, number];
        })
        ?.filter((p): p is [number, number] => Boolean(p)) ?? []
    );
  }, [detail?.locationList]);

  const orangePath = useMemo(() => {
    const path: [number, number][] = [];
    if (startCoord) path.push(startCoord);
    path.push(...trackCoordinates);
    return path.length > 1 ? path : [];
  }, [startCoord, trackCoordinates]);

  const grayPath = useMemo(() => {
    const lastTrackPoint = trackCoordinates?.[trackCoordinates.length - 1];
    if (lastTrackPoint && endCoord) {
      return [lastTrackPoint, endCoord];
    }
    return [];
  }, [trackCoordinates, endCoord]);

  const loop = useCallback(() => {
    timer.current = setInterval(async () => {
      try {
        const lastLocation = detailRef?.current?.locationList?.[detailRef?.current?.locationList?.length - 1];
        const { code, data } = await WaybillService.getTrackingLocationUsingGET(id, lastLocation?.id);
        if (code === API_CODE.SUCCESS && data) {
          if (data.length === 1 && data[0].id === lastLocation?.id) {
            return;
          }

          setDetail((prev) => ({ ...prev, locationList: [...(prev?.locationList ?? []), ...data] }));
        }
      } catch (error) {
        console.error(error);
      }
    }, 60 * 1000);
  }, [detailRef, id, setDetail]);

  useEffect(() => {
    const fetchData = async () => {
      const { code, data } = await WaybillService.getWaybillTrackingLogUsingGET(id);
      if (code === API_CODE.SUCCESS) {
        setDetail(data);

        if (![WAYBILL_STATUS.SIGNED].includes(data?.orderDetail?.waybillStatus as WAYBILL_STATUS)) {
          loop();
        }
      }
    };

    fetchData();
    return () => {
      if (timer.current) clearInterval(timer.current);
    };
  }, [id, loop, setDetail]);

  useEffect(() => {
    if (!map) return;

    if (detail?.locationList?.length) {
      const lastLocation = detail.locationList[detail.locationList.length - 1];
      const lng = Number(lastLocation?.longitude ?? 0);
      const lat = Number(lastLocation?.latitude ?? 0);
      if (Number.isFinite(lng) && Number.isFinite(lat)) {
        map.setCenter([lng, lat]);
      }
      return;
    }

    const fallback = startCoord || endCoord;
    if (fallback) {
      const [lng, lat] = fallback;
      map.setCenter([lng, lat]);
    }
  }, [map, detail?.locationList, startCoord, endCoord]);

  useEffect(() => {
    if (!map) return;

    const upsertLine = (id: string, coords: [number, number][], color: string) => {
      if (coords.length < 2) {
        if (map.getLayer(id)) map.removeLayer(id);
        if (map.getSource(id)) map.removeSource(id);
        return;
      }

      const data = {
        type: 'Feature' as const,
        geometry: {
          type: 'LineString' as const,
          coordinates: coords,
        },
        properties: {},
      };

      const existingSource = map.getSource(id) as GeoJSONSource | undefined;
      if (existingSource) {
        existingSource.setData(data);
      } else {
        map.addSource(id, {
          type: 'geojson',
          data,
        });
        map.addLayer({
          id,
          type: 'line',
          source: id,
          layout: { 'line-cap': 'round', 'line-join': 'round' },
          paint: { 'line-color': color, 'line-width': 4, 'line-opacity': 1 },
        });
      }
    };

    upsertLine('track-path', orangePath, '#FB7703');
    upsertLine('track-to-end', grayPath, '#4C4C4C');

    return () => {
      ['track-path', 'track-to-end'].forEach((id) => {
        if (map.getLayer(id)) map.removeLayer(id);
        if (map.getSource(id)) map.removeSource(id);
      });
    };
  }, [map, orangePath, grayPath]);

  return (
    <>
      {locations.length > 0 &&
        locations
          .filter((poi: Poi) => Boolean(poi.location))
          .map((poi: Poi) => (
            <Marker key={poi?.key} coordinates={poi.location as [number, number]} anchor='bottom'>
              <img
                src={poi.key === 'start' ? '/images/map/start.png' : '/images/map/end.png'}
                className='w-9 h-9'
                alt={poi.key}
              />
            </Marker>
          ))}
      {currentLocation && (
        <Marker
          key='current'
          coordinates={[currentLocation?.longitude ?? 0, currentLocation?.latitude ?? 0]}
          anchor='bottom'
          offset={[0, 18]}
        >
          <img
            src={'/images/map/car.png'}
            alt={''}
            title=''
            className='w-9 h-9'
            style={{ transform: `rotate(${heading}deg)` }}
          />
        </Marker>
      )}
      <Detail id={id} data={detail} />
      {detail?.routeInfoList?.length && <RouteInfo data={detail} />}
    </>
  );
};

const Index = () => {
  const { id } = useParams<{ id: string }>();

  return (
    <Container>
      <MapContent id={id ?? ''} />
    </Container>
  );
};

export default Index;
